import React from "react";
import { AppSidebar, NavItem } from "./navigation/AppSidebar";

// Module-level so the component type is referentially stable across renders.
// An inline `({children}) => <>{children}</>` fallback here was OTO-158: a new
// component type per render made React unmount/remount the entire <Outlet/>
// subtree on every AppLayout render, resetting all page-level state.
const PassthroughBoundary = ({ children }: { children: React.ReactNode }) => <>{children}</>;

import type { OrganizationSummary } from "./navigation/OrganizationSwitcher";
import { UniversalSearchProvider } from "@charphq/ui/providers/universal-search-provider";
import { UniversalSearch } from "@charphq/ui/components/search/UniversalSearch";
import { getEnvVar } from "@charphq/ui/utils/environment";
import { cn } from "../../lib/utils";

export interface AppLayoutProps {
  children?: React.ReactNode;
  isAuthenticated?: boolean;
  user?: {
    name?: string;
    email?: string;
  };
  /** Current user's role for filtering nav items */
  userRole?: string;
  onSignIn?: () => void;
  onSignOut?: () => void;
  Link?: React.ComponentType<{
    to: string;
    className?: string;
    children: React.ReactNode;
    onClick?: () => void;
  }>;
  errorBoundary?: React.ComponentType<{
    children: React.ReactNode;
  }>;
  /** Custom navigation items */
  navItems?: NavItem[];
  /** Organizations the user belongs to (for the active-org switcher). */
  organizations?: OrganizationSummary[];
  /** The currently active organization id. */
  activeOrganizationId?: string;
  /** Switch the active organization. */
  onSwitchOrganization?: (organizationId: string) => void;
}

export function AppLayout({
  children,
  isAuthenticated = false,
  user,
  userRole,
  onSignIn,
  onSignOut,
  Link,
  errorBoundary: ErrorBoundary,
  navItems,
  organizations,
  activeOrganizationId,
  onSwitchOrganization,
}: AppLayoutProps) {
  // Use provided error boundary or default to just rendering children.
  // Both branches must be stable component types (see PassthroughBoundary).
  const RouteErrorBoundary = ErrorBoundary || PassthroughBoundary;

  const universalSearchEnv = getEnvVar("VITE_ENABLE_UNIVERSAL_SEARCH");
  const showUniversalSearch = Boolean(universalSearchEnv);
  const isDev = getEnvVar("NODE_ENV") === "development" || getEnvVar("MODE") === "development";

  return (
    <UniversalSearchProvider>
      {/* [ sidebar | scrollable white workspace ] shell */}
      <div className="flex min-h-screen flex-col bg-background lg:flex-row">
        {/* Dark-indigo navigation rail */}
        <AppSidebar
          items={navItems}
          isAuthenticated={isAuthenticated}
          user={user}
          userRole={userRole}
          onSignIn={onSignIn}
          onSignOut={onSignOut}
          Link={Link}
          organizations={organizations}
          activeOrganizationId={activeOrganizationId}
          onSwitchOrganization={onSwitchOrganization}
        />

        {/* Workspace — consistent padding when signed in; the login screen
            (signed out) renders edge-to-edge and centres itself. */}
        <main
          className={cn(
            "min-w-0 flex-1 bg-background",
            isAuthenticated && "px-4 py-6 sm:px-6 lg:px-8",
          )}
        >
          <RouteErrorBoundary>{children}</RouteErrorBoundary>
        </main>

        {/* Universal Search - only show when authenticated */}
        {showUniversalSearch && isAuthenticated && <UniversalSearch />}

        {/* Dev tools for development */}
        {isDev && (
          <div className="fixed bottom-4 right-4 z-50">
            {/* Devtools will be provided by the consuming application */}
          </div>
        )}
      </div>
    </UniversalSearchProvider>
  );
}
